import { useEffect, useState } from 'react';
import MainContainer from '../components/layout/MainContainer';
import MoldOrderList from '@/components/MoldOrderList';
import ErrorPage from '../components/pages/Error';
import { useMoldOrders } from '../hooks/useMoldOrders';
import { useNavigate } from 'react-router-dom';
import TableListSkeleton from '../components/shared/TableListSkeleton';
import { usePreferences } from '../context/PreferencesContext';
import { ROUTES } from '../constants/routes';
import { useAppState } from '../context/AppStateContext';
import { getMoldOrderSearchFields, getDefaultMoldOrderSearchField } from '../components/KanbanBoard/utils/columnConfigurations';

const MoldOrderListContainer: React.FC = () => {
    const { moldOrders, loading, error, fetchMoldOrders } = useMoldOrders();
    const { preferences } = usePreferences();
    const { companyCode } = useAppState();
    const navigate = useNavigate();

    // Get company-specific search fields
    const moldOrderSearchFields = getMoldOrderSearchFields(companyCode);
    const defaultSearchField = getDefaultMoldOrderSearchField(companyCode);

    // Search state
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSearchField, setSelectedSearchField] = useState(defaultSearchField);

    useEffect(() => {
        // Trigger a re-fetch when the status query parameter changes
        fetchMoldOrders();
    }, [fetchMoldOrders]);

    const handleSearch = (query: string, field: string) => {
        setSearchTerm(query);
        setSelectedSearchField(field);
    };

    const handleSearchFieldChange = (field: string) => {
        setSelectedSearchField(field);
    };

    const handleCreateNewOrder = () => {
        navigate(ROUTES.CREATE_MOLD_ORDERS_MENU);
    };

    // Link back to the default (v2) list. This is the backup page, so it's always shown so the
    // user can return. Preserves the current search text/field as URL query params.
    const handleTryNewList = () => {
        const params = new URLSearchParams();
        if (searchTerm) params.set('q', searchTerm);
        if (selectedSearchField) params.set('qField', selectedSearchField);
        const query = params.toString();
        navigate(query ? `${ROUTES.MOLD_ORDERS}?${query}` : ROUTES.MOLD_ORDERS);
    };

    const TryNewListHeader = () => (
        <button
            type="button"
            onClick={handleTryNewList}
            className="text-sm text-blue-600 hover:text-blue-800 hover:underline whitespace-nowrap"
        >
            รายการแบบใหม่
        </button>
    );

    if (loading) {
        return (
            <MainContainer
                enableSearch={true}
                searchFields={moldOrderSearchFields}
                onSearch={handleSearch}
                onSearchFieldChange={handleSearchFieldChange}
                enableScan={true}
                primaryAction={{
                    label: 'สร้างใบงานใหม่',
                    onClick: handleCreateNewOrder,
                    variant: 'primary'
                }}
                customHeader={<TryNewListHeader />}
            >
                <TableListSkeleton showSearchBar={false} />
            </MainContainer>
        );
    }

    if (error) return <ErrorPage />;

    return (
        <MainContainer
            title='ทะเบียนใบงาน (เวอร์ชันเดิม)'
            enableSearch={true}
            searchFields={moldOrderSearchFields}
            onSearch={handleSearch}
            onSearchFieldChange={handleSearchFieldChange}
            enableScan={false}
            primaryAction={{
                label: 'สร้างใบงานใหม่',
                onClick: handleCreateNewOrder,
                variant: 'primary'
            }}
            customHeader={<TryNewListHeader />}
        >
            <MoldOrderList
                moldOrders={moldOrders}
                preferences={preferences}
                searchTerm={searchTerm}
                selectedSearchField={selectedSearchField}
            />
        </MainContainer>
    );
};

export default MoldOrderListContainer;
